import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { extractServerMessage } from '@cdw-ae/common-data';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { PlatformAdminsService } from '../../common/services/platform-admins/platform-admins.service';
import * as PlatformAdminsActions from './platform-admins.actions';

/**
 * Turns an HTTP failure into a friendly, user-facing message: the server's own
 * message when it sent one (shared extractor — handles string/array/ValidationPipe
 * shapes), otherwise a status-based fallback.
 */
function extractError(err: unknown): string {
  if (err instanceof HttpErrorResponse) {
    const backend = extractServerMessage(err.error);
    if (backend) {
      return backend;
    }
    if (err.status === 403) {
      return 'You are not authorized to manage platform admins.';
    }
  }
  return 'Something went wrong. Please try again.';
}

@Injectable()
export class PlatformAdminsEffects {
  private actions$ = inject(Actions);
  private service = inject(PlatformAdminsService);

  loadAdmins$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlatformAdminsActions.loadAdmins),
      switchMap(() =>
        this.service.list().pipe(
          map((admins) => PlatformAdminsActions.loadAdminsSuccess({ admins })),
          catchError((err) =>
            of(PlatformAdminsActions.loadAdminsFailure({ error: extractError(err) }))
          )
        )
      )
    )
  );

  addAdmin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlatformAdminsActions.addAdmin),
      mergeMap(({ email, name, isSuper }) =>
        this.service.add(email, name, isSuper).pipe(
          map((admin) => PlatformAdminsActions.addAdminSuccess({ admin })),
          catchError((err) =>
            of(PlatformAdminsActions.addAdminFailure({ error: extractError(err) }))
          )
        )
      )
    )
  );

  removeAdmin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlatformAdminsActions.removeAdmin),
      mergeMap(({ id }) =>
        this.service.remove(id).pipe(
          map(() => PlatformAdminsActions.removeAdminSuccess({ id })),
          catchError((err) =>
            of(PlatformAdminsActions.removeAdminFailure({ error: extractError(err) }))
          )
        )
      )
    )
  );
}
